import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { getDomesticHelperQuote } from "../../../../api/domesticHelperServices";
import arrowForward from "../../../../assets/images/arrow_forward_button.svg";
import useDomesticHelperFlow from "../../../../hooks/useDomesticHelperFlowSimple";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import {
  applyPromoCodeToQuote,
  calculatePromoDiscount,
} from "../../../../utils/promoCodeUtils";
import {
  fetchNationalitiesList,
  selectNationalities,
} from "../../../../redux/features/constants/nationalitiesSlice";
import {
  selectDomesticHelperFormData,
  selectDomesticHelperQuoteData,
  setQuoteData,
  setQuoteError,
  setQuoteLoading,
} from "../../../../redux/features/domesticHelperFormSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { selectHelperDetails } from "../../../../redux/features/domesticHelperFlowSlice";
import { policyPay } from "../../../../redux/features/payment/policyPaySlice";
import {
  fetchPartyCode,
  selectPartyCode,
} from "../../../../redux/features/quoteSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import Button from "../../../common/Button/Button";
import SummaryContent from "../../../common/SummaryContent/SummaryContent";
import DomesticHelperJourneyLayout from "../../layouts/DomesticHelperJourneyLayout";

const Summary = () => {
  const dispatch = useDispatch();
  const formData = useSelector(selectDomesticHelperFormData);
  const proposerBasicInfo =
    useSelector(selectHelperDetails)?.basicInfo || null;
  const { handleNextSubstep, handlePrevSubstep } = useDomesticHelperFlow();
  const translate = useLanguageTranslate();

  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [promoAppliedQuoteData, setPromoAppliedQuoteData] = useState(null); // Store quote data with promo applied
  const [isApplyingPromo, setIsApplyingPromo] = useState(false); // Loading state for promo code application

  // Get login info and party code from Redux
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const partyCode = useSelector(selectPartyCode);
  const isLoggedIn = !!loginTokenInfo;

  // Track if we've already re-fetched quote after login
  const hasRefetchedAfterLogin = useRef(false);

  // Get data from Redux store - using correct form names
  const applicationData =
    formData?.DOMESTIC_HELPER_APPLICATION_DETAILS_FORM || {};
  const aboutHelperData = formData?.ABOUT_YOUR_HELPER_FORM || {};

  // Get quote data from Redux (from About Your Helper page API call)
  const quoteData = useSelector(selectDomesticHelperQuoteData);

  // Get nationality data from API via Redux
  const nationalityOptions = useSelector(selectNationalities);

  // Fetch nationalities on component mount
  useEffect(() => {
    if (nationalityOptions.length === 0) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions.length]);

  // Fetch party code for logged-in users
  useEffect(() => {
    if (isLoggedIn && !partyCode && loginTokenInfo?.cpr) {
      console.log(
        "Domestic Helper Summary - Fetching party code for logged-in user:",
        loginTokenInfo.cpr
      );
      dispatch(fetchPartyCode(loginTokenInfo.cpr));
    }
  }, [isLoggedIn, partyCode, loginTokenInfo?.cpr, dispatch]);

  // CRITICAL: Re-fetch quote when guest logs in on summary page
  useEffect(() => {
    const shouldRefetchQuote = async () => {
      // Only re-fetch if:
      // 1. User is now logged in
      // 2. We have a party code (not FALCON)
      // 3. We have existing quote data
      // 4. We haven't already re-fetched after login
      const hasValidPartyCode = partyCode && partyCode !== "FALCON";
      const hasQuote = quoteData && quoteData.data;

      console.log("🔍 Summary page quote refetch check:", {
        isLoggedIn,
        hasValidPartyCode,
        hasQuote,
        hasRefetched: hasRefetchedAfterLogin.current,
        partyCode
      });

      if (
        isLoggedIn &&
        hasValidPartyCode &&
        hasQuote &&
        !hasRefetchedAfterLogin.current
      ) {
        // Get current form data (don't add to dependencies to avoid re-renders)
        const currentAboutHelperData = formData?.ABOUT_YOUR_HELPER_FORM || {};
        const currentApplicationData = formData?.DOMESTIC_HELPER_APPLICATION_DETAILS_FORM || {};

        if (!currentAboutHelperData || Object.keys(currentAboutHelperData).length === 0) {
          console.log("⏭️ SUMMARY: No helper data available yet - skipping refetch");
          return;
        }

        console.log("🔄 SUMMARY: User logged in - re-fetching quote with party code:", partyCode);

        // Mark as refetched to prevent duplicate calls
        hasRefetchedAfterLogin.current = true;

        try {
          dispatch(setQuoteLoading(true));

          toast.info(
            translate(
              "Updating quote with your account information...",
              "جاري تحديث التسعيرة بمعلومات حسابك..."
            )
          );

          console.log("=== Re-calling Domestic Helper Quote API (Summary Page) ===");
          console.log("Using party code:", partyCode);

          const response = await getDomesticHelperQuote(
            currentAboutHelperData,
            currentApplicationData,
            partyCode,
            { timeout: 30000 },
            proposerBasicInfo
          );

          console.log("API Quote Success (After Login):", response);
          dispatch(setQuoteData(response));

          toast.success(
            translate(
              "Quote updated successfully!",
              "تم تحديث التسعيرة بنجاح!"
            )
          );
        } catch (error) {
          console.error("Quote API Error (After Login):", error);
          dispatch(setQuoteError(error.message));

          toast.error(
            translate(
              "Failed to update quote. Please try again.",
              "فشل في تحديث التسعيرة. يرجى المحاولة مرة أخرى."
            )
          );
        }
      }
    };

    shouldRefetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoggedIn,
    partyCode,
    // Note: formData and quoteData intentionally not in deps to prevent re-renders
    // We check them inside but use ref to prevent duplicate calls
  ]);

  // Use API data directly - NO frontend calculations
  const apiPremium = quoteData?.data?.premium
    ? parseFloat(quoteData.data.premium)
    : 0;
  const apiTax = quoteData?.data?.tax ? parseFloat(quoteData.data.tax) : 0;
  const apiTotalPremium = quoteData?.data?.totalPremium
    ? parseFloat(quoteData.data.totalPremium)
    : 0;
  const quoteNumber = quoteData?.data?.quoteNumber || "N/A";

  // Create plan data for summary using API values
  // CRITICAL: Use promo-applied values if promo code was applied
  const planData = {
    premium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Premium
      : apiPremium,
    tax: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Tax
      : apiTax,
    totalPremium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.TotalPremium
      : apiTotalPremium,
    quoteNumber: quoteNumber,
    basePremium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Premium
      : apiPremium,
  };

  // Helper function to translate policy period
  const translatePolicyPeriod = (policyPeriod) => {
    switch (policyPeriod) {
      case "1_year":
        return translate("1 Year", "سنة واحدة");
      case "2_years":
        return translate("2 Years", "سنتان");
      default:
        return translate("2 Years", "سنتان"); // Default fallback
    }
  };

  // Build domestic helper details table data to match design
  const buildDomesticHelperData = () => {
    const data = {};

    // Helper Name from about helper form
    if (aboutHelperData?.fullName) {
      data.helperName = aboutHelperData.fullName;
    }

    // Helper Nationality - get the full name from the nationality list
    if (aboutHelperData?.nationality) {
      const nationalityValue = aboutHelperData.nationality;

      // Find nationality by matching the code (numeric) or value (string)
      const nationalityObj = nationalityOptions.find((nat) => {
        // If stored value is a number, match against code
        if (typeof nationalityValue === "number") {
          return nat.code === nationalityValue;
        }
        // If stored value is a string, try matching code as string or value
        return (
          String(nat.code) === String(nationalityValue) ||
          nat.value === nationalityValue ||
          nat.label === nationalityValue
        );
      });

      // Use the value/label (nationality name) if found, otherwise show the raw value
      data.nationality =
        nationalityObj?.value || nationalityObj?.label || nationalityValue;
    }

    // Date of Birth - format from individual fields
    if (
      aboutHelperData?.dateOfBirth?.day &&
      aboutHelperData?.dateOfBirth?.month &&
      aboutHelperData?.dateOfBirth?.year
    ) {
      const { day, month, year } = aboutHelperData.dateOfBirth;
      data.dateOfBirth = `${day}/${month}/${year}`;
    }

    // Application Type - capitalize first letter
    if (applicationData?.applicationType) {
      const appType = applicationData.applicationType;
      // Capitalize first letter and handle underscores (e.g., "visa_renewal" -> "Visa Renewal")
      data.applicationType = appType
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    } else {
      data.applicationType = "New Application";
    }

    // Working Period - from application details (policyPeriod field)
    if (applicationData?.policyPeriod) {
      data.workingPeriod = translatePolicyPeriod(applicationData.policyPeriod);
    } else if (
      applicationData?.contractDuration ||
      applicationData?.workingPeriod
    ) {
      data.workingPeriod =
        applicationData.contractDuration || applicationData.workingPeriod;
    } else {
      data.workingPeriod = translate("2 Years", "سنتان"); // Default as shown in design
    }

    return data;
  };

  const domesticHelperData = buildDomesticHelperData();

  const applyPromoCode = async () => {
    setIsApplyingPromo(true);

    try {
      const result = await applyPromoCodeToQuote(quoteNumber, promoCode, translate);

      if (result.success) {
        // Calculate discount
        const discount = calculatePromoDiscount(apiTotalPremium, result.data.TotalPremium);

        setPromoCodeApplied(true);
        setPromoCodeDiscount(discount);
        setPromoAppliedQuoteData(result.data);

        toast.success(
          translate(
            "Promo code applied!",
            "تم تطبيق كود الخصم!"
          )
        );
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Promo code application exception:', error);
      toast.error(
        translate(
          "Failed to apply promo code. Please try again.",
          "فشل تطبيق كود الخصم. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoCodeApplied(false);
    setPromoCodeDiscount(0);
    setPromoAppliedQuoteData(null); // Clear promo applied quote data
  };

  const handleProceedToPayment = async () => {
    if (!agreedToTerms) {
      alert(
        translate(
          "Please agree to the terms and conditions",
          "يرجى الموافقة على الشروط والأحكام"
        )
      );
      return;
    }

    // Validate we have the necessary payment data
    if (!quoteNumber || !apiTotalPremium) {
      toast.error(
        translate(
          "Unable to process payment. Quote information is missing.",
          "غير قادر على معالجة الدفع. معلومات التسعيرة مفقودة."
        )
      );
      console.error("Missing payment data:", {
        quoteNumber,
        totalPremium: apiTotalPremium,
      });
      return;
    }

    // CRITICAL: Set redirect flag IMMEDIATELY to prevent any screen flash
    sessionStorage.setItem("isRedirectingToPayment", "true");
    console.log("🔒 Set payment redirect flag IMMEDIATELY on button click");

    // CRITICAL: Use promo-applied quote number if promo code was successfully applied
    // As per API documentation: "Please use the PolicyNumber from the response to send to policy/pay when the request succeeds"
    const finalQuoteNumber = promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.PolicyNumber
      : quoteNumber;

    const finalPremium = promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.TotalPremium
      : apiTotalPremium;

    console.log('=== Payment Preparation ===');
    console.log('Promo Code Applied:', promoCodeApplied);
    console.log('Original Quote Number:', quoteNumber);
    console.log('Final Quote Number (for payment):', finalQuoteNumber);
    console.log('Original Premium:', apiTotalPremium);
    console.log('Final Premium (for payment):', finalPremium);

    const payload = {
      PolicyNumber: finalQuoteNumber,
      Premium: finalPremium,
      Channel: getClientSource(),
    };

    setIsLoading(true);

    try {
      console.log("Initiating payment with payload:", payload);

      // Call the payment API
      const linkAction = await dispatch(policyPay(payload));
      const link = unwrapResult(linkAction);

      console.log("Payment link received:", link);

      if (link) {
        // Redirect to payment gateway
        window.location.href = link;
      } else {
        // Clear flag on error
        sessionStorage.removeItem("isRedirectingToPayment");
        toast.error(
          translate(
            "Unable to generate payment link. Please try again.",
            "غير قادر على إنشاء رابط الدفع. يرجى المحاولة مرة أخرى."
          )
        );
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      // Clear flag on error
      sessionStorage.removeItem("isRedirectingToPayment");
      toast.error(
        translate(
          "Payment failed. Please try again.",
          "فشل الدفع. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <DomesticHelperJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
      >
        <div className="flex flex-col items-center p-6 max767:p-0 mx-auto pt-0 max767:pt-2 py-5 w-full pb-0 max767:pb-0">
          <div className="max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0 w-full max-w-2xl">
            {/* Header */}
            <div className="text-center max767:text-left max767:rtl:text-right mb-8">
              <h1 className="text-2xl font-semibold text-[#1A2024] mb-2 text-center max767:text-left max767:rtl:text-right">
                {translate("Review and Confirm", "المراجعة والتأكيد")}
              </h1>
              <p className="text-gray-600  text-center max767:text-left max767:rtl:text-right">
                {translate(
                  "Please review your information and proceed to payment.",
                  "يرجى مراجعة معلوماتك والانتقال إلى الدفع."
                )}
              </p>
            </div>

            {/* Policy Section */}
            <div className="mb-8">
              {/* Domestic Helper Details Table */}
              <div className="bg-[#F6F8F9] max767:bg-white rounded-xl p-4 max767:p-0 mb-6">
                <h2 className="text-base font-semibold text-[#1A2024] mb-2">
                  {translate(
                    "New Domestic Helper Insurance Policy",
                    "وثيقة تأمين جديدة للعامل المنزلي"
                  )}
                </h2>
                <p className="text-sm mb-4 text-[#1A2024]">
                  {translate(
                    "Review the details below and confirm the payment",
                    "قم بمراجعة التفاصيل أدناه وتأكيد الدفع"
                  )}
                </p>

                {/* Domestic Helper Details Table - with proper border separator */}
                <div className="bg-white border border-[#E5E7EB] rounded-[10px] overflow-hidden">
                  {domesticHelperData.helperName && (
                    <div className="flex border-b border-[#E5E7EB]">
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px] rtl:border-r-0">
                        <span className="text-[#6E7C87] text-sm font-medium">
                          {translate("Helper Name", "اسم العامل")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white px-4 h-[44px] leading-[44px]">
                        <span className="text-[#1A2024] text-sm font-medium">
                          {domesticHelperData.helperName}
                        </span>
                      </div>
                    </div>
                  )}
                  {domesticHelperData.nationality && (
                    <div className="flex border-b border-[#E5E7EB]">
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px] rtl:border-r-0">
                        <span className="text-[#6E7C87] text-sm font-medium">
                          {translate("Nationality", "الجنسية")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white px-4 h-[44px] leading-[44px]">
                        <span className="text-[#1A2024] text-sm font-medium">
                          {domesticHelperData.nationality}
                        </span>
                      </div>
                    </div>
                  )}
                  {domesticHelperData.dateOfBirth && (
                    <div className="flex border-b border-[#E5E7EB]">
                      <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px] rtl:border-r-0">
                        <span className="text-[#6E7C87] text-sm font-medium">
                          {translate("Date of birth", "تاريخ الميلاد")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white px-4 h-[44px] leading-[44px]">
                        <span className="text-[#1A2024] text-sm font-medium">
                          {domesticHelperData.dateOfBirth}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex border-b border-[#E5E7EB]">
                    <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px] rtl:border-r-0">
                      <span className="text-[#6E7C87] text-sm font-medium">
                        {translate("Application Type", "نوع الطلب")}
                      </span>
                    </div>
                    <div className="flex-1 bg-white px-4 h-[44px] leading-[44px]">
                      <span className="text-[#1A2024] text-sm font-medium">
                        {translate(
                          domesticHelperData.applicationType,
                          "طلب جديد"
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex">
                    <div className="flex-1 px-4 bg-white border-r border-[#E5E7EB] rtl:border-l h-[44px] leading-[44px] rtl:border-r-0">
                      <span className="text-[#6E7C87] text-sm font-medium">
                        {translate("Working Period", "فترة العمل")}
                      </span>
                    </div>
                    <div className="flex-1 bg-white px-4 h-[44px] leading-[44px]">
                      <span className="text-[#1A2024] text-sm font-medium">
                        {domesticHelperData.workingPeriod}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="mb-8">
              <div className="bg-[#F6F8F9] rounded-xl p-4">
                <SummaryContent
                  planData={planData}
                  promoCode={promoCode}
                  setPromoCode={setPromoCode}
                  applyPromoCode={applyPromoCode}
                  clearPromoCode={clearPromoCode}
                  promoCodeApplied={promoCodeApplied}
                  promoCodeDiscount={promoCodeDiscount}
                  isApplyingPromo={isApplyingPromo}
                  showQuotationNumber={true}
                  showPromoCode={true}
                  className="!bg-transparent"
                  flowType="domesticHelper"
                />
              </div>
            </div>

            {/* Policy Notice */}
            <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-start space-x-3 rtl:space-x-reverse">
              <div className="flex-shrink-0 mt-0.5">
                <svg
                  className="w-5 h-5 text-[#A56213]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[#252C32] font-semibold text-sm">
                  {translate(
                    "Kindly note that the policy will be issued under your name",
                    "يرجى ملاحظة أن الوثيقة ستصدر باسمك"
                  )}
                </p>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-6">
              <label className="flex items-start space-x-3 rtl:space-x-reverse cursor-pointer">
                <label class="flex items-center cursor-pointer gap-2">
                  <input
                    type="checkbox"
                    class="peer hidden"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                  />
                  <div
                    class="size-5 border-2 border-gray-400 rounded-[4px] 
              peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7] 
              flex items-center justify-center transition-all"
                  >
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </label>
                {/* <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 size-5 text-[#00A9E7] bg-gray-100 border-gray-300 rounded focus:ring-[#00A9E7] focus:ring-2"
                /> */}
                <div className="text-sm text-[#252C32] rtl:font-medium leading-[24px]">
                  <span>
                    {translate(
                      "I hereby certify that the above given information is true and correct to the best of my knowledge, and I agree to cooperate fully by providing all requested documents before payment. I understand that providing invalid information will result in my insurance policy to be void. I also read and agree the",
                      "أقر بموجب هذا أن المعلومات المقدمة أعلاه صحيحة ودقيقة حسب علمي، وأوافق على التعاون الكامل من خلال تقديم جميع الوثائق المطلوبة قبل الدفع. أفهم أن تقديم معلومات غير صحيحة سيؤدي إلى إلغاء وثيقة التأمين الخاصة بي. كما أنني قرأت وأوافق على"
                    )}
                  </span>{" "}
                  <button
                    className="text-[#00384D] underline font-bold hover:text-[#0088CC]"
                    onClick={() =>
                      window.open(
                        "https://content.solidarity.bh/uploads/TRAVEL_INSURANCE_WORDING_30_08_2023_8e74afd369_44e9596879.pdf",
                        "_blank"
                      )
                    }
                  >
                    {translate("Policy Specification", "مواصفات وثيقة")}
                  </button>{" "}
                  <span>
                    {translate("of this Insurance product.", "التأمين هذه.")}
                  </span>
                </div>
              </label>
            </div>

            {/* Proceed to Payment Button - Desktop */}
            <div className="max767:hidden">
              <Button
                onSubmit={handleProceedToPayment}
                disabled={!agreedToTerms || isLoading}
                isLoading={isLoading}
                label={translate("Proceed to Payment", "انتقل إلى الدفع")}
                post_icon={arrowForward}
                post_icon_class={isRTL ? "rotate-180 gap-2" : ""}
                className="w-full mt-6 py-4 px-6 rounded-[10px] gap-2"
              />
            </div>
          </div>
        </div>

        {/* Proceed to Payment Button - Mobile (Fixed at Bottom) */}
        <div className="hidden max767:block fixed bottom-0 left-0 w-full bg-white border-t border-[#E5E9EB] p-4 pb-6 z-[9] max767:pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Button
            onSubmit={handleProceedToPayment}
            disabled={!agreedToTerms || isLoading}
            isLoading={isLoading}
            label={translate("Proceed to Payment", "انتقل إلى الدفع")}
            fontSize="text-lg max767:text-sm"
            post_icon={arrowForward}
            post_icon_class={isRTL ? "rotate-180" : ""}
            className="w-full py-4 px-6 rounded-[10px] max767:h-[40px] max767:p-0 max767:justify-center"
          />
        </div>
      </DomesticHelperJourneyLayout>
    </div>
  );
};

export default Summary;
